import * as C from '../common/constants';
import * as ROUTES from '../common/routes';
import Button from './Button';
import PrevNextAnswer from './PrevNextAnswer';
import React, { useEffect, useRef, useState } from 'react';
import SearchInput from './SearchInput';
import Tooltip from '@mui/material/Tooltip';
import Tree from './Tree';
import clsx from 'clsx';
import html2canvas from 'html2canvas';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { ReactComponent as IcoClose } from '../assets/svg/icon_close.svg';
import { jsPDF } from 'jspdf';
import html2PDF from 'jspdf-html2canvas';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  useTranslations,
  useDynamicTranslations,
} from '../common/hooks/useTranslations';

interface IProps {
  children: any;
  themeTitle?: string;
  uri?: string;
  backUrl?: string;
  showPrevNext?: boolean;
  printData?: any;
}

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      contentLayoutContainerStyle: {
        width: '100%',
        // height: '100%',
        minHeight: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        padding: '30px',
        boxSizing: 'border-box',
        flex: '1 1 100%',
      },
      contentLayoutWrapperStyle: {
        width: '100%',
        // maxWidth: `${C.LEFT_CONTENT_MAX_WIDTH}px`,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        paddingRight: '30px',
        flex: '1 1 100%',
      },
      titleWrapperStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      titleStyle: {
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '20px',
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        whiteSpace: 'nowrap',
        marginBottom: '10px',
      },
      iconWrapperStyle: {
        width: '20px',
        height: '20px',
        minWidth: '20px',
        minHeight: '20px',
        // borderRadius: '50%',
        // border: `1px solid ${COLORS.textGray}`,
        // background: COLORS.textGray,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: '70px',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: '0.7',
            transition: '0.5s ease',
          },
        },
      },
      iconStyle: {
        width: '20px',
        fill: COLORS.appBlue,
      },
      contentLayoutContentStyle: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
      },
      themeTreeStyle: {
        width: '100%',
        maxWidth: '220px',
        minWidth: '220px',
        // maxWidth: '240px',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        alignItems: 'center',
      },
      buttonMapStyle: {
        fontWeight: FONT_WEIGHT.bold,
        fontSize: '16px',
        fontFamily: FONTS.alegreya,
        color: COLORS.appBlue,
        margin: '0 0 28px 0',
        textDecoration: 'underline',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      clickStyle: {
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            opacity: 0.7,
            transition: '0.5s ease',
          },
        },
      },
      buttonsWrapperStyle: {
        display: 'flex',
        flexDirection: 'row',
      },
      pdfButtonStyle: {
        width: '70px',
        minWidth: '70px',
        marginBottom: '20px',
      },
      [`@media (max-width: 840px)`]: {
        contentLayoutContainerStyle: {
          flexDirection: 'column-reverse',
          padding: '30px 20px 0 20px',
        },
        contentLayoutWrapperStyle: {
          padding: 0,
        },
        themeTreeStyle: {
          maxWidth: '100%',
        },
        buttonMapStyle: {
          alignSelf: 'flex-end',
        },
        iconWrapperStyle: {
          margin: 0,
        },
        titleWrapperStyle: {
          flexDirection: 'column',
        },
      },
      [`@media (max-width: 410px)`]: {
        contentLayoutContainerStyle: {
          padding: '20px 20px 0 20px',
        },
      },
    })
);

const ContentLayout = ({
  children,
  themeTitle,
  uri,
  backUrl,
  showPrevNext,
  printData,
}: IProps): JSX.Element => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();

  const buttonMapLabel = useTranslations('map_button');
  const closeTooltip = useDynamicTranslations('close_tooltip');

  const contentRef = useRef(null);
  const printRef = useRef(null);

  const isPDF = pathname.includes('/pdf/');

  const onMapClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    navigate({
      pathname: ROUTES.MAP,
      search,
    });
  };

  const onThemeClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    navigate({
      pathname: uri,
      search,
    });
  };

  const isClickable = Boolean(uri);
  const clickProps = { ...(isClickable ? { onClick: onThemeClick } : {}) };

  const onBackClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    navigate({
      pathname: backUrl,
      search,
    });
  };

  const getPrintName = () => {
    if (printData) {
      const { themeName, surveyName, date } = printData;
      const rawName = `${themeName}__${surveyName}__${date}`;
      const rawName2 = rawName.replace(/\s+/g, '_');
      const rawName3 = rawName2.toLowerCase();
      const pdfName = rawName3.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      return pdfName + '.pdf';
    }

    return 'gadsimta_stasti.pdf';
  };

  const onPDFClick = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const element = printRef.current;
    const contentElement = contentRef.current;

    contentElement.style.display = 'none';
    element.style.display = 'flex';

    const canvas = await html2canvas(element);

    contentElement.style.display = 'flex';
    element.style.display = 'none';

    const imgData = canvas.toDataURL('image/png');

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    const doc = new jsPDF('p', 'mm', 'a4');
    let position = 10;

    doc.addImage(imgData, 'PNG', 10, position, imgWidth - 20, imgHeight);
    heightLeft -= pageHeight - 20;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight + 10;
      doc.addPage();
      doc.addImage(imgData, 'PNG', 10, position, imgWidth - 20, imgHeight);
      heightLeft -= pageHeight;
    }

    const pdfName = getPrintName();

    doc.save(pdfName);
  };

  return (
    <div className={classes.contentLayoutContainerStyle}>
      <div className={classes.contentLayoutWrapperStyle} ref={contentRef}>
        {isPDF && (
          <Button
            text={'PDF'}
            onClick={onPDFClick}
            className={classes.pdfButtonStyle}
          />
        )}
        <div className={classes.titleWrapperStyle}>
          {themeTitle && (
            <div
              className={clsx(
                classes.titleStyle,
                isClickable && classes.clickStyle
              )}
              {...clickProps}
            >
              {themeTitle}
            </div>
          )}
          <div className={classes.buttonsWrapperStyle}>
            {showPrevNext && <PrevNextAnswer />}
            {Boolean(backUrl) && (
              <Tooltip title={closeTooltip} placement={'top-end'} arrow>
                <div className={classes.iconWrapperStyle} onClick={onBackClick}>
                  <IcoClose className={classes.iconStyle} />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        <div className={classes.contentLayoutContentStyle}>{children}</div>
      </div>

      <div
        style={{ display: 'none' }}
        className={classes.contentLayoutWrapperStyle}
        ref={printRef}
      >
        <div className={classes.titleWrapperStyle}>
          {themeTitle && (
            <div
              className={clsx(
                classes.titleStyle,
                isClickable && classes.clickStyle
              )}
            >
              {themeTitle}
            </div>
          )}
        </div>

        <div className={classes.contentLayoutContentStyle}>{children}</div>
      </div>

      <div className={classes.themeTreeStyle}>
        <div className={classes.buttonMapStyle} onClick={onMapClick}>
          {buttonMapLabel}
        </div>
        <SearchInput />
        <Tree />
      </div>
    </div>
  );
};

export default ContentLayout;
